import type { ReactElement } from 'react';
import { useEffect, useRef } from 'react';
import type { BridgeConnectionSteps } from '../types.js';
import { BridgeConnectionStepsIndicator } from './connection-steps.js';
import { FunnelShell } from './funnel-shell.js';
import { useStepView } from '../hooks/use-step-view.js';
import { Sel, STagNew, STagType } from '#~/stag/index.js';
import { onboardingContextFor, useOnboardingTracker } from '../hooks/use-onboarding-tracker.js';

interface FunnelDetectingProps {
  /** One-based Bridge readiness attempt, including Try Again passes. */
  readonly attemptNumber: number;
  readonly steps: BridgeConnectionSteps;
  /** True after the user left a Bridge failure through Try Again. */
  readonly recovering?: boolean | undefined;
  // Per ENG-3796 (Didier ITB #1): dismiss handler. Detecting was the one screen
  // with no way out — passing this through to FunnelShell's onEscape renders the
  // persistent close (X) and re-enables the Escape key, so the user is never
  // trapped while the bridge connects.
  /** Escape / the close affordance: dismiss WITHOUT recording the permanent
   *  skip. Distinct from onSkip, which is an explicit user decision to defer —
   *  see gate.tsx handleDismiss (ENG-3796 live-run defect 1). */
  readonly onDismiss: () => void;
}

// Screen 3: real bridge connection progress via depot/broker signals.
// Heading receives focus on mount so screen readers announce the transition
// (accessibility audit finding — no focusable element on this passive screen).
export const FunnelDetecting = ({
  attemptNumber,
  steps,
  recovering = false,
  onDismiss
}: FunnelDetectingProps): ReactElement => {
  useStepView('detecting');
  const tracker = useOnboardingTracker();

  const headingRef = useRef<HTMLHeadingElement>(null);
  const startedRef = useRef(false);
  const recoveredRef = useRef(false);
  useEffect(() => {
    headingRef.current?.focus();
    if (startedRef.current) return;
    startedRef.current = true;
    // A view says the screen rendered. A start says the Bridge checks began,
    // which is the denominator for timeout and recovery rates.
    tracker.trackStepStarted(onboardingContextFor('detecting'), {
      attempt_number: attemptNumber
    });
  }, [attemptNumber, tracker]);

  useEffect(() => {
    if (!recovering || recoveredRef.current) return;
    if (!steps.agentDetected || !steps.bridgeAuthenticated || !steps.roomLinked) return;
    recoveredRef.current = true;
    tracker.trackRecovered(onboardingContextFor('detecting'), {
      reason: 'bridge_ready_after_retry',
      attempt_number: attemptNumber
    });
  }, [attemptNumber, recovering, steps, tracker]);

  return (
    <FunnelShell announceTitle='Connecting your Bridge' onEscape={onDismiss}>
      <h1
        ref={headingRef}
        tabIndex={-1}
        className='text-2xl font-demibold text-white outline-none'
        {...STagNew(Sel.OnboardingDetecting, 'heading', undefined, STagType.Data)}
      >
        Connecting your Bridge
      </h1>
      <p className='mt-12 text-md text-muted'>This usually takes a few seconds.</p>

      <div className='mt-32 w-full max-w-[24rem]'>
        <BridgeConnectionStepsIndicator steps={steps} />
      </div>
    </FunnelShell>
  );
};
